import dashboardService from '../../../services/dashboardService';
import {
    setWidgetLoading,
    setWidgetError,
    setStats,
    setLegacyStats,
    setYearlySales,
    setSalesByLocation,
    setBranchComparison,
    setTopEmployees,
    setRecentCustomers,
    setRecentPayments,
    setRecentStockUsage,
    setRevenueTrend,
    setPaymentMethodsSummary,
    setStockUsageTrend,
    setDateRange,
    setCustomerFilters,
    setPaymentFilters,
    setSelectedYear,
} from './dashboardSlice';

const toError = (error, fallback) => ({
    message: error.message || fallback,
    errors: error.errors || {},
    status: error.status || 500,
});

const cleanParams = (filters) => {
    const params = {};
    Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
            params[key] = value;
        }
    });
    return params;
};

export const fetchStats = () => async (dispatch) => {
    try {
        dispatch(setWidgetLoading({ key: 'stats', value: true }));
        const response = await dashboardService.getStats();
        dispatch(setStats(response.data || response));
        return response;
    } catch (error) {
        dispatch(setWidgetError({ key: 'stats', error: toError(error, 'Failed to fetch dashboard statistics') }));
        throw error;
    }
};

export const fetchLegacyStats = () => async (dispatch) => {
    try {
        dispatch(setWidgetLoading({ key: 'legacyStats', value: true }));
        const response = await dashboardService.getLegacyStats();
        dispatch(setLegacyStats(response.data || response));
        return response;
    } catch (error) {
        dispatch(setWidgetError({ key: 'legacyStats', error: toError(error, 'Failed to fetch legacy dashboard statistics') }));
        throw error;
    }
};

export const fetchYearlySales = (year = null) => async (dispatch) => {
    try {
        dispatch(setWidgetLoading({ key: 'yearlySales', value: true }));
        if (year) {
            dispatch(setSelectedYear(year));
        }
        const response = await dashboardService.getYearlySales(year);
        dispatch(setYearlySales(response.data || response));
        return response;
    } catch (error) {
        dispatch(setWidgetError({ key: 'yearlySales', error: toError(error, 'Failed to fetch yearly sales data') }));
        throw error;
    }
};

export const fetchSalesByLocation = () => async (dispatch) => {
    try {
        dispatch(setWidgetLoading({ key: 'salesByLocation', value: true }));
        const response = await dashboardService.getSalesByLocation();
        dispatch(setSalesByLocation(response.data || response));
        return response;
    } catch (error) {
        dispatch(setWidgetError({ key: 'salesByLocation', error: toError(error, 'Failed to fetch sales by location') }));
        throw error;
    }
};

export const fetchBranchComparison = () => async (dispatch) => {
    try {
        dispatch(setWidgetLoading({ key: 'branchComparison', value: true }));
        const response = await dashboardService.getBranchComparison();
        dispatch(setBranchComparison({
            data: response.data || [],
            branches: response.branches || [],
        }));
        return response;
    } catch (error) {
        dispatch(setWidgetError({ key: 'branchComparison', error: toError(error, 'Failed to fetch branch comparison') }));
        throw error;
    }
};

export const fetchTopEmployees = () => async (dispatch) => {
    try {
        dispatch(setWidgetLoading({ key: 'topEmployees', value: true }));
        const response = await dashboardService.getTopEmployees();
        dispatch(setTopEmployees(response.data || []));
        return response;
    } catch (error) {
        dispatch(setWidgetError({ key: 'topEmployees', error: toError(error, 'Failed to fetch top employees') }));
        throw error;
    }
};

export const fetchRecentCustomers = (filters) => async (dispatch, getState) => {
    try {
        dispatch(setWidgetLoading({ key: 'recentCustomers', value: true }));
        const activeFilters = filters || getState().dashboard.customerFilters;
        const response = await dashboardService.getRecentCustomers(cleanParams(activeFilters));
        dispatch(setRecentCustomers(response.data || []));
        return response;
    } catch (error) {
        dispatch(setWidgetError({ key: 'recentCustomers', error: toError(error, 'Failed to fetch recent customers') }));
        throw error;
    }
};

export const fetchRecentPayments = (filters) => async (dispatch, getState) => {
    try {
        dispatch(setWidgetLoading({ key: 'recentPayments', value: true }));
        const activeFilters = filters || getState().dashboard.paymentFilters;
        const response = await dashboardService.getRecentPayments(cleanParams(activeFilters));
        dispatch(setRecentPayments(response.data || []));
        return response;
    } catch (error) {
        dispatch(setWidgetError({ key: 'recentPayments', error: toError(error, 'Failed to fetch recent payments') }));
        throw error;
    }
};

export const fetchRecentStockUsage = () => async (dispatch) => {
    try {
        dispatch(setWidgetLoading({ key: 'recentStockUsage', value: true }));
        const response = await dashboardService.getRecentStockUsage();
        dispatch(setRecentStockUsage(response.data || []));
        return response;
    } catch (error) {
        dispatch(setWidgetError({ key: 'recentStockUsage', error: toError(error, 'Failed to fetch recent stock usage') }));
        throw error;
    }
};

export const updateCustomerFilters = (partial) => (dispatch, getState) => {
    dispatch(setCustomerFilters(partial));
    const filters = { ...getState().dashboard.customerFilters, ...partial };
    return dispatch(fetchRecentCustomers(filters));
};

export const updatePaymentFilters = (partial) => (dispatch, getState) => {
    dispatch(setPaymentFilters(partial));
    const filters = { ...getState().dashboard.paymentFilters, ...partial };
    return dispatch(fetchRecentPayments(filters));
};

export const changeYear = (year) => (dispatch) => {
    dispatch(setSelectedYear(year));
};

export const fetchRevenueTrend = () => async (dispatch, getState) => {
    try {
        dispatch(setWidgetLoading({ key: 'revenueTrend', value: true }));
        const { dateRange } = getState().dashboard;
        const response = await dashboardService.getRevenueTrend(cleanParams(dateRange));
        dispatch(setRevenueTrend(response.data || []));
        return response;
    } catch (error) {
        dispatch(setWidgetError({ key: 'revenueTrend', error: toError(error, 'Failed to fetch revenue trend') }));
        throw error;
    }
};

export const fetchPaymentMethodsSummary = () => async (dispatch, getState) => {
    try {
        dispatch(setWidgetLoading({ key: 'paymentMethodsSummary', value: true }));
        const { dateRange } = getState().dashboard;
        const response = await dashboardService.getPaymentMethodsSummary(cleanParams(dateRange));
        dispatch(setPaymentMethodsSummary(response));
        return response;
    } catch (error) {
        dispatch(setWidgetError({ key: 'paymentMethodsSummary', error: toError(error, 'Failed to fetch payment methods summary') }));
        throw error;
    }
};

export const fetchStockUsageTrend = () => async (dispatch, getState) => {
    try {
        dispatch(setWidgetLoading({ key: 'stockUsageTrend', value: true }));
        const { dateRange } = getState().dashboard;
        const response = await dashboardService.getStockUsageTrend(cleanParams(dateRange));
        dispatch(setStockUsageTrend(response));
        return response;
    } catch (error) {
        dispatch(setWidgetError({ key: 'stockUsageTrend', error: toError(error, 'Failed to fetch stock usage trend') }));
        throw error;
    }
};

export const applyDateRange = (dateRange) => (dispatch) => {
    dispatch(setDateRange(dateRange));
    dispatch(fetchStats());
    dispatch(fetchRevenueTrend());
    dispatch(fetchPaymentMethodsSummary());
    dispatch(fetchStockUsageTrend());
    dispatch(fetchRecentCustomers());
    dispatch(fetchRecentPayments());
};
